import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Button, Icon, Tooltip, Typography } from 'shared/ui'
import { DayjsFormats, numberFormat } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { GracePeriodAlert, ImportNumbersStatusStore } from 'entities/Phone'
import { useOverviewPageContext } from 'pages/settings/pages/billing/pages/overview/context/overviewContext'
import { getIntervalLabel } from 'pages/settings/pages/billing/pages/overview/ui/PlanInfo/store/planInfoStore'
import styles from './styles.module.scss'

const TRIAL_TOOLTIP_DESC =
  "When trial ends, you'll automatically upgrade to the $49/month plan. You can upgrade or cancel your trial anytime before then."

export const PlanInfo = observer(() => {
  const entities = useEntitiesContext()
  const { subscriptionStore, billingStore, organizationStore } = entities
  const { planInfoStore, renewalDate } = useOverviewPageContext()
  const [importNumbersStatusStore] = useState(() => new ImportNumbersStatusStore(organizationStore))

  useEffect(() => {
    return () => {
      importNumbersStatusStore.dispose()
    }
  }, [])

  const { importNumbersStatus, hasValidImportNumbersStatus } = importNumbersStatusStore
  const isCustomPlan = subscriptionStore.plan?.custom ?? false
  const subscriptionPlan = subscriptionStore.plan
  const isTrial = subscriptionStore.isTrial
  const isPAYG = subscriptionStore.isPAYGPlan

  if (!subscriptionPlan) {
    return null
  }

  const billingPlan = billingStore.billingPlansStore.getItem(subscriptionPlan.id)
  const planInfo = (() => {
    if (isCustomPlan) return billingStore.billingPlansStore.customPlanInfo
    if (isTrial) return billingStore.billingPlansStore.trialPlanInfo
    if (isPAYG) return billingStore.billingPlansStore.PAYGPlanInfo

    return billingStore.billingPlansStore.getPlanInfo(
      subscriptionPlan.id,
      subscriptionPlan.interval
    )
  })()

  if (!planInfo) {
    return null
  }

  const interval = getIntervalLabel(subscriptionPlan.interval)
  const credits = numberFormat({ value: subscriptionStore.planCredits ?? 0 })
  const additionalCreditPrice = numberFormat({
    value: subscriptionPlan.additional_credit_amount,
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })

  const isDowngrade = subscriptionStore.scheduledIsDowngrade
  const scheduledPlanId = subscriptionStore.scheduledPlanId
  const scheduledPlan = scheduledPlanId
    ? billingStore.billingPlansStore.getItem(scheduledPlanId)
    : null
  const subscriptionSuggestion = subscriptionStore.subscriptionSuggestion
  const hasValidTrialEndsAt = Boolean(organizationStore.trialEndsAt)
  const trialDaysRemaining = hasValidTrialEndsAt ? organizationStore.trialEndsAtDaysNumber : null
  const trialEndsDate = hasValidTrialEndsAt
    ? uiStore.dayjs(organizationStore.trialEndsAt).format(DayjsFormats.full6)
    : null

  const getScheduleLabel = () => {
    if (isDowngrade) {
      return `Downgrade on ${renewalDate}`
    }

    if (scheduledPlan) {
      return `Upgrade to ${scheduledPlan.labelCreditsByIntervalAndPlanType} on ${renewalDate}`
    }

    if (subscriptionStore.isScheduledCancellation) {
      return `Cancel subscription on ${renewalDate}`
    }

    return `Renewal on ${renewalDate}`
  }

  const getPlanName = () => {
    if (isCustomPlan) return 'Custom Plan'
    if (isTrial) return 'Trial Plan'
    if (isPAYG) return 'Pay As You Go'

    return billingPlan?.labelCreditsByIntervalAndPlanType ?? ''
  }

  const renderPlanDetails = () => {
    if (isTrial) {
      return (
        <>
          <Typography ariaLabel='plan_credits' variant='body-md-regular'>
            25 trial credits
          </Typography>
          {trialDaysRemaining !== null && trialEndsDate && (
            <>
              <div className={styles.separator} />
              <div className={styles.trialEndsInfo}>
                <Typography ariaLabel='plan_trial_ends' variant='body-md-regular'>
                  Trial ends in {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'}
                </Typography>
                <Tooltip
                  type='description'
                  label={`Auto-upgrade on ${trialEndsDate}.`}
                  desc={TRIAL_TOOLTIP_DESC}
                  placement='bottom'
                  maxWidth={404}
                >
                  <Icon icon='question' fontSize={16} color='var(--content-neutral-primary)' />
                </Tooltip>
              </div>
            </>
          )}
        </>
      )
    }

    if (isPAYG) {
      return (
        <>
          <Typography ariaLabel='plan_credits' variant='body-md-regular'>
            0 credits / month
          </Typography>
          <div className={styles.separator} />
          <Typography ariaLabel='plan_additional_credit_price' variant='body-md-regular'>
            {additionalCreditPrice} per additional credit
          </Typography>
        </>
      )
    }

    return (
      <>
        <Typography ariaLabel='plan_credits' variant='body-md-regular'>
          {credits} credits / {interval}
        </Typography>
        <div className={styles.separator} />
        <Typography ariaLabel='plan_additional_credit_price' variant='body-md-regular'>
          {additionalCreditPrice} per additional credit
        </Typography>
        <div className={styles.separator} />
        <Typography ariaLabel='plan_renewal_date' variant='body-md-regular'>
          {getScheduleLabel()}
        </Typography>
      </>
    )
  }

  return (
    <div className={styles.planHeader}>
      <Typography
        ariaLabel='upgrade_billing'
        variant='body-xs-regular'
        color='var(--gray-80)'
        className={styles.billingLabel}
      >
        Plan & Billing
      </Typography>
      <div className={styles.planRow}>
        <div className={styles.iconAndName}>
          <Icon icon={planInfo.icon} fontSize={32} color={planInfo.iconColor} />
          <Typography ariaLabel='plan_name' variant='heading-lg'>
            {getPlanName()}
          </Typography>
        </div>
        <Button
          id={planInfoStore.upgradeButtonId}
          text={'Upgrade plan'}
          intent='positive'
          size='medium'
          onClick={planInfoStore.onUpgradeClick}
        />
      </div>
      <div className={styles.planDetails}>{renderPlanDetails()}</div>

      <div className={styles.alertsContainer}>
        {hasValidImportNumbersStatus && importNumbersStatus.isActive && (
          <GracePeriodAlert
            importNumbersStatus={importNumbersStatus}
            rightActions={[
              {
                variant: 'button',
                type: 'primary',
                text: 'Manage numbers',
                onClick: planInfoStore.onManageNumbersClick,
              },
            ]}
          />
        )}
        {isDowngrade && scheduledPlan && (
          <Alert
            item={{
              type: 'warning',
              title: `You'll be downgraded to "${scheduledPlan?.labelCreditsByIntervalAndPlanType}" on ${renewalDate}.`,
              desc: "Unused credits from your current plan won't roll over.",
            }}
            rightActions={[
              {
                variant: 'button',
                type: 'primary',
                text: 'Cancel downgrade',
                onClick: planInfoStore.onCancelDowngradeClick,
              },
            ]}
          />
        )}

        {subscriptionStore.isScheduledCancellation && renewalDate && (
          <Alert
            item={{
              type: 'warning',
              title: `Your subscription will be canceled on ${renewalDate}. You won't be billed again.`,
              desc: 'To avoid losing access and data, keep your subscription active.',
            }}
            rightActions={[
              {
                variant: 'button',
                type: 'primary',
                text: 'Keep my plan',
                onClick: planInfoStore.onKeepPlanClick,
              },
            ]}
          />
        )}
        {subscriptionSuggestion &&
          !organizationStore.isClient &&
          !planInfoStore.isSuggestionAlertDismissed && (
            <Alert
              item={{
                type: 'warning',
                desc: "You're paying more than you need to. Upgrade to the right plan and save more.",
              }}
              rightActions={[
                {
                  variant: 'button',
                  type: 'primary',
                  text: 'See offer',
                  onClick: planInfoStore.onSeeOfferClick,
                },
              ]}
              onClose={planInfoStore.onDismissSuggestionAlert}
            />
          )}
      </div>
    </div>
  )
})
